import React from 'react';
import {
  EdgeProps,
  getBezierPath,
  EdgeLabelRenderer,
  BaseEdge,
} from 'reactflow';

interface AnimatedEdgeProps extends EdgeProps {
  data?: {
    isAnimated?: boolean;
    animationSpeed?: number;
    color?: string;
    isExecuting?: boolean;
    executionProgress?: number; // 0-1 között
  };
}

const AnimatedEdge: React.FC<AnimatedEdgeProps> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  data,
  markerEnd,
}) => {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const isAnimated = data?.isAnimated || false;
  const isExecuting = data?.isExecuting || false;
  const executionProgress = data?.executionProgress || 0;
  const animationSpeed = data?.animationSpeed || 2;
  const edgeColor = data?.color || '#6b7280';


  return (
    <>
      {/* Alap edge (háttér) */}
      <BaseEdge
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          ...style,
          fill: 'none', // Fontos: nincs kitöltés!
          stroke: isExecuting ? '#e5e7eb' : edgeColor,
          strokeWidth: 2,
          opacity: isExecuting ? 0.3 : 1,
        }}
      />

      {/* Animált flow (normál animáció) */}
      {isAnimated && !isExecuting && (
        <path
          d={edgePath}
          style={{
            fill: 'none',
            stroke: edgeColor,
            strokeWidth: 3,
            strokeDasharray: '10 10',
            opacity: 0.8,
          }}
          className="flow-edge-idle"
        />
      )}

      {/* Execution progress (csak egy pont fut végig) */}
      {isExecuting && executionProgress > 0 && executionProgress < 1 && (
        <>
          {/* Fading trail */}
          <circle
            cx={sourceX + (targetX - sourceX) * Math.max(0, executionProgress - 0.1)}
            cy={sourceY + (targetY - sourceY) * Math.max(0, executionProgress - 0.1)}
            r="4"
            fill="#10b981"
            opacity="0.4"
          />
          <circle
            cx={sourceX + (targetX - sourceX) * Math.max(0, executionProgress - 0.05)}
            cy={sourceY + (targetY - sourceY) * Math.max(0, executionProgress - 0.05)}
            r="5"
            fill="#10b981"
            opacity="0.7"
          />
          
          {/* Main progress dot */}
          <circle
            cx={sourceX + (targetX - sourceX) * executionProgress}
            cy={sourceY + (targetY - sourceY) * executionProgress}
            r="6"
            fill="#10b981"
            className="execution-progress-dot"
            style={{
              filter: 'drop-shadow(0 0 12px rgba(16, 185, 129, 1))',
            }}
          />
        </>
      )}

      {/* Success state (végrehajtás befejezve) */}
      {isExecuting && executionProgress >= 1 && (
        <path
          d={edgePath}
          className="flow-edge-completed"
          style={{ fill: 'none' }}
        />
      )}

      {/* Error state */}
      {data?.color === '#ef4444' && (
        <path
          d={edgePath}
          className="flow-edge-error"
          style={{ fill: 'none' }}
        />
      )}

      {/* Edge label (opcionális) */}
      <EdgeLabelRenderer>
        {data?.isExecuting && (
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              fontSize: 10,
              pointerEvents: 'all',
            }}
            className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium shadow-lg"
          >
            {Math.round(executionProgress * 100)}%
          </div>
        )}
      </EdgeLabelRenderer>
    </>
  );
};

export default AnimatedEdge;
